/**
 * Marketplace Analytics list page.
 *
 * SuperAdmin sees every active marketplace basket; publishers see only the
 * baskets they themselves published; AccountManagers see baskets at least one
 * of their managed users is subscribed to (with counts scoped to that managed
 * subset). Backend (`/analytics/marketplace/baskets`) enforces the gate; we
 * just render whatever it returns and surface a banner explaining the scope.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle, ChevronRight, Home, LineChart as LineChartIcon,
  RefreshCw, Search,
} from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import MarketplaceAnalyticsTabs from '../components/marketplace/MarketplaceAnalyticsTabs';
import MarketplaceDateRangeStrip from '../components/marketplace/MarketplaceDateRangeStrip';
import { useAuth } from '../context/AuthContext';
import marketplaceAnalyticsService from '../services/marketplaceAnalyticsService';
import { cn } from '../utils/cn';
import { marketplaceDefault30d } from '../utils/dateUtils';
import { formatInr, formatPct, pnlClass } from '../utils/platformFormat';
import type {
  AnalyticsRole,
  DateRangePreset,
  MarketplaceListingSummary,
} from '../types/marketplaceAnalytics';

const VALID_PRESETS: DateRangePreset[] = [
  'today', '7d', '30d', '90d', 'fy', 'ytd', '1y', 'all', 'custom',
];

type SortKey = 'title' | 'active_subscribers' | 'subscribers_count'
  | 'cagr' | 'win_rate' | 'sharpe' | 'published_at'
  // Phase 5A: rollup-derived columns.
  | 'capital_deployed' | 'capital_my' | 'capital_wide'
  | 'trades_total' | 'live_net_pnl';
type SortDir = 'asc' | 'desc';

const MarketplaceAnalyticsListPage: React.FC = () => {
  const { isAdmin, isSuperAdmin, isAccountManager, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  // Single bool the gate uses everywhere. Mirrors the backend's
  // _require_authorized: SuperAdmin OR Admins OR AccountManagers.
  const canViewAnalytics = isAdmin || isSuperAdmin || isAccountManager;

  const [role, setRole] = useState<AnalyticsRole>('other');
  const [additionalRoles, setAdditionalRoles] = useState<AnalyticsRole[]>([]);
  const [rows, setRows] = useState<MarketplaceListingSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dual-group caller (e.g. publisher + AM) — banner copy + per-card
  // labeling key off this. Derived for clarity (NOT memoized — booleans).
  const isDualGroup = additionalRoles.length > 0;

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [showUnpublished, setShowUnpublished] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('active_subscribers');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // URL-driven date range so refresh / share round-trips. Default preset =
  // 30d so the first-load "Trades" / "Live P&L" columns match the detail
  // page's default aggregate window.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDefaults = useMemo(() => marketplaceDefault30d(), []);
  const preset = useMemo<DateRangePreset>(() => {
    const raw = searchParams.get('preset');
    return (raw && (VALID_PRESETS as string[]).includes(raw))
      ? (raw as DateRangePreset) : '30d';
  }, [searchParams]);
  const startParam = searchParams.get('start') || initialDefaults.start;
  const endParam = searchParams.get('end') || initialDefaults.end;

  // Cancellation token: rapid preset toggles fire overlapping requests.
  // Only the most-recent fetch's resolution wins. Mirrors the detail
  // page's fetchTokenRef pattern.
  const fetchTokenRef = React.useRef(0);

  const fetchPage = useCallback(async (params: {
    start: string; end: string; preset: DateRangePreset;
  }) => {
    fetchTokenRef.current += 1;
    const myToken = fetchTokenRef.current;
    setIsLoading(true);
    setError(null);
    try {
      // "All" preset skips the window params so the backend serves the
      // cheap lifetime path (single BatchGetItem) instead of the N-Query
      // window fanout. The strip persists preset in the URL for
      // refresh/share continuity; the backend only needs start/end.
      const outbound = params.preset === 'all'
        ? { preset: params.preset }
        : params;
      const resp = await marketplaceAnalyticsService.list(outbound);
      if (myToken !== fetchTokenRef.current) return;
      setRows(resp.baskets || []);
      setRole(resp.role);
      setAdditionalRoles(resp.additional_roles || []);
    } catch (err: any) {
      if (myToken !== fetchTokenRef.current) return;
      setError(err?.message || 'Failed to load marketplace analytics');
      setRows([]);
    } finally {
      if (myToken === fetchTokenRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!canViewAnalytics) return;
    fetchPage({ start: startParam, end: endParam, preset });
    // Bump the token on cleanup so any in-flight fetch's setState is
    // discarded when the component unmounts or the effect re-fires.
    return () => {
      fetchTokenRef.current += 1;
    };
  }, [authLoading, canViewAnalytics, fetchPage, startParam, endParam, preset]);

  const categories = useMemo(() => {
    const out = new Set<string>();
    for (const r of rows) if (r.category) out.add(r.category);
    return Array.from(out).sort();
  }, [rows]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (needle && !`${r.title} ${r.publisher_email}`.toLowerCase().includes(needle)) return false;
      if (category && r.category !== category) return false;
      if (!showUnpublished && !r.is_published) return false;
      return true;
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    // num() defends against null / undefined / NaN — degraded rows AND
    // legacy listings can present those for any numeric column. Critical
    // for backtest_summary.* which is optional on legacy rows (the render
    // path uses ?. — sort must match).
    const num = (v: any) => {
      if (v == null) return Number.NEGATIVE_INFINITY;
      const n = Number(v);
      return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
    };
    // S11: when BOTH values are the missing-sentinel, the difference
    // (-Inf) - (-Inf) is NaN — V8 sort treats NaN as 0 but the comparator
    // becomes non-transitive across a set of mixed-missing rows, producing
    // implementation-defined ordering. Wrapping the diff in `cmp()` returns
    // an explicit 0 for the both-missing case so sort stability holds.
    const cmp = (av: number, bv: number) => {
      if (!Number.isFinite(av) && !Number.isFinite(bv)) return 0;
      return (av - bv) * dir;
    };
    out.sort((a, b) => {
      switch (sortKey) {
        case 'title':
          return (a.title || '').localeCompare(b.title || '') * dir;
        case 'active_subscribers':
          return cmp(num(a.active_subscribers), num(b.active_subscribers));
        case 'subscribers_count':
          // T2: sort the 'Total subs' column by `total_subscribers` —
          // the actual value rendered in the cell. The legacy
          // `subscribers_count` field on the card is the basket-wide
          // platform count (publisher's stored value), which diverges
          // from the displayed managed-user-subset count on AM cards.
          return cmp(num(a.total_subscribers), num(b.total_subscribers));
        case 'cagr':
          return cmp(num(a.backtest_summary?.cagr), num(b.backtest_summary?.cagr));
        case 'win_rate':
          return cmp(num(a.backtest_summary?.win_rate), num(b.backtest_summary?.win_rate));
        case 'sharpe':
          return cmp(num(a.backtest_summary?.sharpe_ratio), num(b.backtest_summary?.sharpe_ratio));
        case 'capital_deployed':
          return cmp(num(a.capital_deployed), num(b.capital_deployed));
        case 'capital_my':
          return cmp(num(a.capital_deployed_my_users), num(b.capital_deployed_my_users));
        case 'capital_wide':
          return cmp(num(a.capital_deployed_basket_wide), num(b.capital_deployed_basket_wide));
        case 'trades_total':
          return cmp(num(a.trades_total), num(b.trades_total));
        case 'live_net_pnl':
          return cmp(num(a.live_net_pnl), num(b.live_net_pnl));
        case 'published_at':
          return (a.published_at || '').localeCompare(b.published_at || '') * dir;
        default:
          return 0;
      }
    });
    return out;
  }, [rows, search, category, showUnpublished, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir(k === 'title' ? 'asc' : 'desc');
    }
  };

  if (authLoading) return null;
  if (!canViewAnalytics) {
    return (
      <StandardLayout>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">Marketplace Analytics requires Admin, AccountManager, or SuperAdmin access.</span>
          </div>
        </div>
      </StandardLayout>
    );
  }

  return (
    <StandardLayout>
      <PageHeader
        title="Marketplace Analytics"
        pageType="management"
        icon={<LineChartIcon className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Platform' },
          {
            label: isSuperAdmin
              ? 'Platform Owner'
              : isDualGroup
                ? 'Listings + Managed Users'
                : role === 'account_manager'
                  ? 'Managed Users'
                  : 'Listings',
          },
        ]}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchPage({ start: startParam, end: endParam, preset })}
            leftIcon={<RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />}
            disabled={isLoading}
          >
            Refresh
          </Button>
        }
      />

      <MarketplaceAnalyticsTabs active="analytics" />

      {/* Date-range strip mirrors the detail page's strip. Trades / Live P&L
          columns respond to the selected window; backtest columns don't
          (they're marketed backtest, window-independent). */}
      <MarketplaceDateRangeStrip
        preset={preset}
        start={startParam}
        end={endParam}
        onChange={(next) => {
          const sp = new URLSearchParams(searchParams);
          sp.set('preset', next.preset);
          sp.set('start', next.start);
          sp.set('end', next.end);
          setSearchParams(sp, { replace: true });
        }}
      />

      {/* Dual-group caller (publisher + AM): explain the union scope so
          cards mixed from both discovery paths are legible. Per-card
          chips below distinguish each row's effective_role.
          T7: count reflects what's actually on screen — visible.length
          tracks the Card title and matches the user's current search/
          category/published filters. Banner stays honest as the filter
          state changes. */}
      {isDualGroup && role === 'publisher'
          && additionalRoles.includes('account_manager') && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">
              Showing {visible.length} basket{visible.length === 1 ? '' : 's'} —
              a mix of baskets you published (full basket-wide stats) and
              baskets your managed users are subscribed to (managed-users-only stats).
              Each row carries a chip indicating which scope it came from.
            </span>
          </div>
        </div>
      )}

      {role === 'publisher' && !isDualGroup && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">
              Showing {visible.length} basket{visible.length === 1 ? '' : 's'} you published.
              SuperAdmins see every basket on the platform.
            </span>
          </div>
        </div>
      )}

      {/* U5: backend always emits `role='publisher'` for dual-group callers
          (Admins + AccountManagers); `role='account_manager'` can ONLY
          appear on a single-role response, so the prior `!isDualGroup`
          guard was unreachable. If the wire contract ever flips, this
          branch will need explicit logic for the dual-group case anyway. */}
      {role === 'account_manager' && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm">
              Showing {visible.length} basket{visible.length === 1 ? '' : 's'} at least one of your
              managed users is subscribed to. Subscriber counts and live P&amp;L on the detail
              page are scoped to your managed users only.
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <span className="text-red-800 dark:text-red-200 text-sm">{error}</span>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Baskets ({visible.length} of {rows.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search title or publisher email…"
                leftIcon={<Search className="h-4 w-4" />}
                size="sm"
                className="w-72"
              />
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
              >
                <option value="">All categories</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={showUnpublished}
                  onChange={(e) => setShowUnpublished(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Show unpublished
              </label>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading…</span>
            </div>
          ) : visible.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              {rows.length === 0
                /* T8: dual-group branch BEFORE the publisher branch —
                   otherwise a dual-group caller (role='publisher') with
                   zero baskets would see the "you have not published"
                   message that silently hides the AM scope. */
                ? (isDualGroup && additionalRoles.includes('account_manager')
                  ? 'You have not published any baskets, and none of your managed users are subscribed to any.'
                  : role === 'publisher'
                    ? 'You have not published any marketplace baskets yet.'
                    : role === 'account_manager'
                      ? 'None of your managed users are subscribed to any marketplace basket yet.'
                      : 'No marketplace baskets found.')
                : 'No baskets match the current filters.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <Th k="title" current={sortKey} dir={sortDir} onClick={toggleSort}>Basket</Th>
                    <Th align="right" k="active_subscribers" current={sortKey} dir={sortDir} onClick={toggleSort}>Active subs</Th>
                    <Th align="right" k="subscribers_count" current={sortKey} dir={sortDir} onClick={toggleSort}>Total subs</Th>
                    {/* Phase 5A: Capital Deployed splits into TWO columns for AM
                        (managed-user subset + basket-wide) and stays as a
                        single column for Publisher/SuperAdmin (basket-wide
                        only — they own/govern the basket). */}
                    {role === 'account_manager' ? (
                      <>
                        <Th align="right" k="capital_my" current={sortKey} dir={sortDir} onClick={toggleSort}>My users' capital</Th>
                        <Th align="right" k="capital_wide" current={sortKey} dir={sortDir} onClick={toggleSort}>Basket-wide capital</Th>
                      </>
                    ) : (
                      <Th align="right" k="capital_deployed" current={sortKey} dir={sortDir} onClick={toggleSort}>Capital deployed</Th>
                    )}
                    <Th align="right" k="trades_total" current={sortKey} dir={sortDir} onClick={toggleSort}>Trades</Th>
                    <Th align="right" k="live_net_pnl" current={sortKey} dir={sortDir} onClick={toggleSort}>Live P&amp;L</Th>
                    <Th align="right" k="cagr" current={sortKey} dir={sortDir} onClick={toggleSort}>Backtest CAGR</Th>
                    <Th align="right" k="win_rate" current={sortKey} dir={sortDir} onClick={toggleSort}>Backtest Win%</Th>
                    <Th align="right" k="sharpe" current={sortKey} dir={sortDir} onClick={toggleSort}>Sharpe</Th>
                    <Th align="right" k="published_at" current={sortKey} dir={sortDir} onClick={toggleSort}>Published</Th>
                    <th className="px-4 py-3 w-8" />
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {visible.map((r) => (
                    <tr
                      key={r.basket_id}
                      className={cn(
                        'hover:bg-gray-50 dark:hover:bg-gray-700',
                        // Degraded rows render but aren't navigable — the
                        // detail page would re-do the failing fan-out and
                        // show the operator nothing useful.
                        r.error
                          ? 'cursor-not-allowed opacity-70'
                          : 'cursor-pointer',
                      )}
                      onClick={() => {
                        if (r.error || !r.basket_id) return;
                        navigate(`/admin/marketplace-analytics/${encodeURIComponent(r.basket_id)}`);
                      }}
                    >
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-gray-900 dark:text-white">{r.title || '(untitled)'}</span>
                          {r.error && (
                            // Degraded row indicator. Surfaces the per-row
                            // server failure that R3+V4 isolated, so the
                            // operator can see WHICH baskets couldn't be
                            // summarized rather than mistaking them for
                            // zero-subscriber baskets.
                            <span
                              title="Failed to load summary for this basket — see CloudWatch logs"
                              className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-200 uppercase"
                            >
                              <AlertCircle className="h-3 w-3 mr-1" />
                              degraded
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {r.publisher_email || r.publisher_user_id || '—'}
                        </div>
                        <div className="mt-1 flex gap-1 flex-wrap">
                          {r.category && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 uppercase">
                              {r.category}
                            </span>
                          )}
                          {r.risk_level && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-200 uppercase">
                              {r.risk_level}
                            </span>
                          )}
                          {!r.is_published && !r.error && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 uppercase">
                              draft
                            </span>
                          )}
                          {/* Dual-group per-card scope chip — only present
                              when the response carries effective_role
                              (S2/S3). Helps the operator distinguish
                              "you published this" rows from "your managed
                              users hold this" rows in the merged list. */}
                          {r.effective_role === 'publisher' && isDualGroup && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 uppercase">
                              your basket
                            </span>
                          )}
                          {r.effective_role === 'account_manager' && isDualGroup && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 uppercase">
                              managed users
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">{r.active_subscribers}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-500 dark:text-gray-400">{r.total_subscribers}</td>
                      {/* Phase 5A capital columns. Renders '—' when the
                          rollup row hasn't been written yet for this basket
                          (brand-new basket / backfill incomplete / flag off). */}
                      {role === 'account_manager' ? (
                        <>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                            {r.capital_deployed_my_users != null
                              ? formatInr(r.capital_deployed_my_users)
                              : '—'}
                          </td>
                          <td
                            className="px-4 py-3 text-right tabular-nums text-gray-500 dark:text-gray-400"
                            title="Total capital across ALL subscribers of this basket (your scope shown to the left)."
                          >
                            {r.capital_deployed_basket_wide != null
                              ? formatInr(r.capital_deployed_basket_wide)
                              : '—'}
                          </td>
                        </>
                      ) : (
                        <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                          {r.capital_deployed != null
                            ? formatInr(r.capital_deployed)
                            : '—'}
                        </td>
                      )}
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                        {r.trades_total != null
                          ? r.trades_total.toLocaleString('en-IN')
                          : '—'}
                      </td>
                      <td className={cn(
                        'px-4 py-3 text-right tabular-nums',
                        r.live_net_pnl != null ? pnlClass(r.live_net_pnl) : 'text-gray-500 dark:text-gray-400',
                      )}>
                        {r.live_net_pnl != null
                          ? formatInr(r.live_net_pnl)
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">{formatPct(r.backtest_summary?.cagr, 1)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">{formatPct(r.backtest_summary?.win_rate, 1)}</td>
                      {/* Legacy listings can land here without a backtest_summary
                          (or with missing nested fields) — null-guard before
                          calling Number.toFixed so a single bad row can't crash
                          the entire table render. */}
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-200">
                        {r.backtest_summary?.sharpe_ratio != null
                          ? r.backtest_summary.sharpe_ratio.toFixed(2)
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-500 dark:text-gray-400 text-xs">
                        {r.published_at ? r.published_at.slice(0, 10) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </StandardLayout>
  );
};

interface ThProps {
  k: SortKey;
  current: SortKey;
  dir: SortDir;
  onClick: (k: SortKey) => void;
  align?: 'left' | 'right';
  children: React.ReactNode;
}
const Th: React.FC<ThProps> = ({ k, current, dir, onClick, align, children }) => (
  <th
    scope="col"
    className={cn(
      'px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-100',
      align === 'right' ? 'text-right' : 'text-left',
    )}
    onClick={() => onClick(k)}
  >
    <span className="inline-flex items-center gap-1">
      {children}
      {current === k && <span>{dir === 'asc' ? '↑' : '↓'}</span>}
    </span>
  </th>
);

export default MarketplaceAnalyticsListPage;
